import type { DocSummary } from "@xop/dashboard-shared";

/**
 * Pure list-filtering logic for the type/release docs page, extracted from
 * `TypeReleaseDocsPage.tsx` so it unit-tests directly (it was previously
 * inlined in `useMemo`s and only exercised through the integration test).
 */

export interface ReleaseScope {
  isUncategorized: boolean;
  isUnversioned: boolean;
  /** Numeric release id, or `null` for the unversioned bucket. */
  releaseNumericId: number | null;
}

/**
 * Narrow a category-scoped doc list to the active TYPE → RELEASE pair. The
 * server filters by category only, so the release scope is applied here:
 * an uncategorized view drops anything that HAS a category, an unversioned
 * view keeps only release-less docs, and a versioned view matches the id.
 */
export function scopeFilter(docs: DocSummary[], scope: ReleaseScope): DocSummary[] {
  return docs.filter((d) => {
    if (scope.isUncategorized && d.category) return false;
    if (scope.isUnversioned) return d.default_release == null;
    return d.default_release?.id === scope.releaseNumericId;
  });
}

/** A doc's solution-area slugs (many-to-many; tolerant of the legacy shape). */
function docAreaSlugs(d: DocSummary): string[] {
  return (d.solution_areas ?? []).map((a) => a.slug);
}

/** Apply the orthogonal solution-area chip filter on top of the release scope.
 * `"all"` passes everything through; `"none"` keeps the un-tagged docs;
 * otherwise a doc matches when ANY of its areas is the selected slug
 * (a doc can carry several since 2026-06-27). */
export function solutionFilter(docs: DocSummary[], activeSolution: string): DocSummary[] {
  if (activeSolution === "all") return docs;
  if (activeSolution === "none") return docs.filter((d) => docAreaSlugs(d).length === 0);
  return docs.filter((d) => docAreaSlugs(d).includes(activeSolution));
}

/** Per-area doc counts for the chip labels — `all` (total), `none` (un-tagged),
 * and one key per KNOWN area slug (zero-filled so a chip can read "(0)" even
 * when empty). A doc with N areas counts under EACH of them. Computed off the
 * release-scoped list, independent of selection. `areas` is the live managed
 * list so an empty pillar still shows a chip. */
export function solutionCounts(
  filtered: DocSummary[],
  areas: ReadonlyArray<{ slug: string }>,
): Record<string, number> {
  const counts: Record<string, number> = { all: filtered.length, none: 0 };
  for (const a of areas) counts[a.slug] = 0;
  for (const d of filtered) {
    const slugs = docAreaSlugs(d);
    if (slugs.length === 0) {
      counts.none = (counts.none ?? 0) + 1;
      continue;
    }
    for (const slug of slugs) counts[slug] = (counts[slug] ?? 0) + 1;
  }
  return counts;
}

/** Sort direction for the "Created" (detected document date) column. `null`
 * = keep the server's order (unsorted). URL-encoded as `?doc_sort=`. */
export type DocDateSort = "date_desc" | "date_asc";

/** Coerce a raw `?doc_sort=` query value to the sort union (or `null`). */
export function parseDocDateSort(raw: string | null | undefined): DocDateSort | null {
  return raw === "date_desc" || raw === "date_asc" ? raw : null;
}

/** The next sort state when the "Created" header is clicked: cycles
 * unsorted → newest-first → oldest-first → unsorted. */
export function cycleDocDateSort(current: DocDateSort | null): DocDateSort | null {
  if (current === "date_desc") return "date_asc";
  if (current === "date_asc") return null;
  return "date_desc";
}

/** Sort a doc list by the detected document date. Docs with NO date always
 * sort LAST (in both directions) so the dash rows sink to the bottom; ties
 * (and the unsorted case) keep the input order (stable). Returns a new array. */
export function sortByDocumentDate(docs: DocSummary[], dir: DocDateSort | null): DocSummary[] {
  if (dir == null) return docs;
  const time = (iso: string | null): number | null => {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    return Number.isNaN(t) ? null : t;
  };
  return docs
    .map((d, i) => ({ d, i, t: time(d.document_date) }))
    .sort((a, b) => {
      if (a.t == null && b.t == null) return a.i - b.i; // both undated → stable
      if (a.t == null) return 1; // nulls last
      if (b.t == null) return -1;
      if (a.t === b.t) return a.i - b.i; // stable tie-break
      return dir === "date_desc" ? b.t - a.t : a.t - b.t;
    })
    .map((x) => x.d);
}

/** The chips to render: All, each known pillar (in the managed list's order),
 * and an Uncategorized chip — the last only when something in the release scope
 * is actually un-tagged. */
export function solutionOptions(
  counts: Record<string, number>,
  areas: ReadonlyArray<{ slug: string; name: string }>,
): { value: string; label: string }[] {
  const opts: { value: string; label: string }[] = [
    { value: "all", label: "All" },
    ...areas.map((a) => ({ value: a.slug, label: a.name })),
  ];
  if ((counts.none ?? 0) > 0) opts.push({ value: "none", label: "Uncategorized" });
  return opts;
}
